import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import urls from "../../config/urls.json";
import { useSession } from "../../hooks/useSession";
import { useStates } from "../../hooks/useStates";
import { ApiService as api } from "../../services/ApiService";
import { getYMD } from "../../services/formatters";

export const useFilialiPage = () => {
    const [currentMapToShow, setCurrentMapToShow] = useState("");
    const navigate = useNavigate();
    const { read, update, batchUpdate, reloadAppOnBack } = useSession();
    const { states, setCurrentState, isFilialiSlideBusy, isCfPageReady } = useStates();
    const [fetchError, setFetchError] = useState(false);
    const [fetchErrorMessage, setFetchErrorMessage] = useState("");

    let subSlider = useRef();
    let daySlider = useRef();
    // Becomes false on AVANTI; remount after browser back creates a fresh true ref
    const isPageActive = useRef(true);
    const currentSlideFiliale = useRef("");
    const giorniRequestId = useRef(0);

    useEffect(() => {
        isPageActive.current = true;
        if (read("id_filiale_direct")) {
            reloadAppOnBack();
        }
        setCurrentState(states.LOAD_FILIALI_SLIDES_START);
        // subSlider.current.go(0);
        onFilialiSlideChange(read("subsForce") ? 0 : read("filialeSceltaIdx"), read("subsForce"));
    }, []);

    const next = () => {
        // Invalidate in-flight GetFiliale/Disponibilita so they can't downgrade state after navigate
        isPageActive.current = false;
        giorniRequestId.current += 1;
        setCurrentState(states.FETCH_CF_END);
        update("subsForce", false);
        navigate(urls.datiPersonaliNonRiconosciuto.path);
    };

    const onFilialiSlideChange = async (filialeIdx, forced = false) => {
        if (!isPageActive.current) return;
        setCurrentState(states.LOAD_FILIALI_SLIDES_START);
        update("orari", []);
        const currFiliale = read("filiali")[filialeIdx];
        const idCurrFiliale = currFiliale.codFiliale;

        currentSlideFiliale.current = idCurrFiliale;
        const filiale = await api.GetFiliale({ codice: idCurrFiliale }, currFiliale);

        if (!isPageActive.current) return;
        if (!forced) {
            if (currentSlideFiliale.current != filiale.codFiliale) return;
        }

        batchUpdate({
            filialeScelta: currFiliale,
            disponibilita: filiale.days,
            dataScelta: filiale.days[0],
        });
        // force change on giorniSlider to automate call to Disponibilita
        // LOAD_FILIALI_SLIDES_END is set by onGiorniSlideChange once Disponibilita completes
        if (daySlider.current) {
            daySlider.current.go(0);
            // if already at index 0, the previous line won't trigger the splide change, hence we need to force it
            if (daySlider.current.splide.index == 0) {
                await onGiorniSlideChange(0);
            }
        } else {
            await onGiorniSlideChange(0);
        }
        if (subSlider.current && forced) {
            subSlider.current.go(0);
        }
    };

    const onGiorniSlideChange = async (giornoIdx) => {
        if (!isPageActive.current) return;
        const requestId = ++giorniRequestId.current;
        setCurrentState(states.LOAD_FILIALI_SLIDES_START);
        update("dataScelta", read("disponibilita")[giornoIdx]);
        update("dayIdx", giornoIdx);
        update("orari", []);

        // call new Disponibilita
        let disponibilitaResult = {};
        try {
            const TipoFiliali = read("prodotto") == 3 ? 1 : 0;
            disponibilitaResult = await api.Disponibilita({
                CodFiliale: read("filialeScelta").codFiliale,
                Giorno: getYMD(read("dataScelta").data),
                TipoFiliali,
            });
        } catch (error) {
            if (requestId !== giorniRequestId.current || !isPageActive.current) return;
            setFetchErrorMessage("C'è stato un errore nel caricamento delle disponibilità delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        // Ignore stale responses (older day/filiale change or user already clicked AVANTI)
        if (requestId !== giorniRequestId.current || !isPageActive.current) return;

        update("orari", disponibilitaResult.orariDisponibili);

        let favouriteHour = { idx: disponibilitaResult.orariDisponibili.findIndex((item) => item.disponibile === true) };
        /*
        let favouriteHour = disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0];
        let eligibleOrarioScelto = [];
        if (read("orarioScelto")) {
            eligibleOrarioScelto = disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true);
            // eligibleOrarioScelto = {idx: disponibilitaResult.orariDisponibili.findIndex(item => item.disponibile === true)};
        }
        if (eligibleOrarioScelto.length > 0) {
            // favouriteHour = eligibleOrarioScelto[0];
            favouriteHour = {idx: disponibilitaResult.orariDisponibili.findIndex(item => item.disponibile === true)};
        }
        */

        update("orarioScelto", favouriteHour);
        update("orarioSceltoId", disponibilitaResult.orariDisponibili[favouriteHour.idx].id);
        update("orarioSceltoLabel", disponibilitaResult.orariDisponibili[favouriteHour.idx].label);
        setCurrentState(states.LOAD_FILIALI_SLIDES_END);
    };

    const showMap = (id, coords) => {
        if (currentMapToShow.length) {
            setCurrentMapToShow("");
            destroyMap(id);
        } else {
            setCurrentMapToShow(id);
            renderMap(id, coords);
        }
    };
    const renderMap = (id, coords) => {
        const appleMapManager = window.AppleMapManager();
        appleMapManager.renderMap(id, {
            latitude: coords.lat,
            longitude: coords.lng,
        });
    };
    const destroyMap = (id) => {
        setTimeout(() => {
            const mapNode = document.getElementById(id);
            mapNode.innerHTML = "";
        }, 500);
    };

    return {
        next,
        read,
        update,
        subSlider,
        daySlider,
        onFilialiSlideChange,
        onGiorniSlideChange,
        isFilialiSlideBusy,
        isCfPageReady,
        showMap,
        currentMapToShow,
        fetchErrorMessage,
        fetchError,
        setFetchError,
    };
};
